import {
  createContext,
  createElement,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useAuth } from '@features/auth/context/AuthContext';
import { normalizeAuthEmail } from '@features/auth/graphql/userRows';
import { resolveShopCustomerViaBff } from '@core/commerce/api/commerceApi';
import {
  maskPhone,
  storedPhoneFromGeneratedFirebaseEmail,
} from '@shared/utils/phoneIdentity';
import {
  clearIdentitySnapshot,
  readIdentitySnapshot,
  writeIdentitySnapshot,
} from './identityStore';
import type {
  IdentityBrokerState,
  IdentityBrokerValue,
  ResolveShopCustomerOptions,
  ShopCustomer,
} from './identityTypes';

const IdentityBrokerContext = createContext<IdentityBrokerValue | undefined>(
  undefined
);

function emptyState(): IdentityBrokerState {
  return {
    globalIdentity: null,
    shopCustomersByShopId: {},
    statusByShopId: {},
    version: 0,
  };
}

export function IdentityBrokerProvider({ children }: { children: ReactNode }) {
  const {
    user,
    userData,
    getUserForShop,
    ensureCustomerForShop,
    userDataByShop,
  } = useAuth();

  const [state, setState] = useState<IdentityBrokerState>(() => emptyState());
  const resolveFlightsRef = useRef<Map<string, Promise<ShopCustomer | null>>>(
    new Map()
  );
  const stateRef = useRef(state);
  stateRef.current = state;

  useEffect(() => {
    if (!user?.uid || !user.email) {
      resolveFlightsRef.current.clear();
      setState(prev => ({
        ...emptyState(),
        version: prev.version + 1,
      }));
      return;
    }

    const generatedEmail = normalizeAuthEmail(user.email);
    const phone = storedPhoneFromGeneratedFirebaseEmail(generatedEmail);
    const snapshot = readIdentitySnapshot(user.uid);
    const nextCustomers = {
      ...snapshot.shopCustomersByShopId,
      ...Object.fromEntries(
        Object.entries(userDataByShop).filter(([, row]) => Boolean(row))
      ),
    } as Record<string, ShopCustomer>;

    const nextStatus = Object.fromEntries(
      Object.keys(nextCustomers).map(shopId => [shopId, 'ready'])
    ) as IdentityBrokerState['statusByShopId'];

    setState(prev => ({
      globalIdentity: {
        firebaseUid: user.uid,
        generatedEmail,
        phone,
        displayName: user.displayName,
      },
      shopCustomersByShopId: nextCustomers,
      statusByShopId: nextStatus,
      version: prev.version + 1,
    }));
  }, [user?.uid, user?.email, user?.displayName, userDataByShop]);

  useEffect(() => {
    if (!user?.uid) return;
    writeIdentitySnapshot(user.uid, state.shopCustomersByShopId);
  }, [user?.uid, state.shopCustomersByShopId]);

  const getShopCustomer = useCallback(
    (shopId: string): ShopCustomer | null => {
      return (
        stateRef.current.shopCustomersByShopId[shopId] ??
        (getUserForShop(shopId) as ShopCustomer | null) ??
        null
      );
    },
    [getUserForShop]
  );

  const resolveShopCustomer = useCallback(
    async (
      shopId: string,
      options?: ResolveShopCustomerOptions
    ): Promise<ShopCustomer | null> => {
      if (!user?.uid || !user.email || !shopId) return null;

      const cached = getShopCustomer(shopId);
      if (cached && !options?.force) return cached;

      const existingFlight = resolveFlightsRef.current.get(shopId);
      if (existingFlight) return existingFlight;

      setState(prev => ({
        ...prev,
        statusByShopId: {
          ...prev.statusByShopId,
          [shopId]: 'resolving',
        },
      }));

      const flight = (async (): Promise<ShopCustomer | null> => {
        let customer: ShopCustomer | null = null;
        const generatedEmail = normalizeAuthEmail(user.email);
        const phone = storedPhoneFromGeneratedFirebaseEmail(generatedEmail);

        if (import.meta.env.VITE_COMMERCE_API_BASE_URL) {
          try {
            const idToken = await user.getIdToken();
            const response = await resolveShopCustomerViaBff(
              {
                shopId,
                phone: phone ?? '',
                email: userData?.email ?? null,
                name: user.displayName,
              },
              idToken
            );
            customer = (response.customer as ShopCustomer | null) ?? null;
          } catch (error) {
            console.warn(
              '[identityBroker] BFF resolve failed; falling back to storefront resolver',
              { shopId, phone: maskPhone(phone), error }
            );
          }
        }

        if (!customer) {
          customer = (await ensureCustomerForShop(shopId)) as ShopCustomer | null;
        }

        return customer;
      })()
        .then(customer => {
          setState(prev => ({
            ...prev,
            shopCustomersByShopId: {
              ...prev.shopCustomersByShopId,
              [shopId]: customer,
            },
            statusByShopId: {
              ...prev.statusByShopId,
              [shopId]: customer ? 'ready' : 'failed',
            },
            version: prev.version + 1,
          }));
          return customer;
        })
        .catch(error => {
          console.error('[identityBroker] resolveShopCustomer failed', {
            shopId,
            error,
          });
          setState(prev => ({
            ...prev,
            statusByShopId: {
              ...prev.statusByShopId,
              [shopId]: 'failed',
            },
            version: prev.version + 1,
          }));
          return null;
        })
        .finally(() => {
          resolveFlightsRef.current.delete(shopId);
        });

      resolveFlightsRef.current.set(shopId, flight);
      return flight;
    },
    [
      ensureCustomerForShop,
      getShopCustomer,
      user,
      userData?.email,
    ]
  );

  const resolveManyShopCustomers = useCallback(
    async (
      shopIds: string[],
      options?: ResolveShopCustomerOptions
    ): Promise<Record<string, ShopCustomer | null>> => {
      const uniqueShopIds = [...new Set(shopIds.filter(Boolean))];
      const entries = await Promise.all(
        uniqueShopIds.map(async shopId => [
          shopId,
          await resolveShopCustomer(shopId, options),
        ] as const)
      );
      return Object.fromEntries(entries);
    },
    [resolveShopCustomer]
  );

  const clearIdentityCache = useCallback(() => {
    if (user?.uid) clearIdentitySnapshot(user.uid);
    resolveFlightsRef.current.clear();
    setState(prev => ({
      ...emptyState(),
      globalIdentity: prev.globalIdentity,
      version: prev.version + 1,
    }));
  }, [user?.uid]);

  const value = useMemo<IdentityBrokerValue>(
    () => ({
      ...state,
      getShopCustomer,
      resolveShopCustomer,
      resolveManyShopCustomers,
      clearIdentityCache,
    }),
    [
      state,
      getShopCustomer,
      resolveShopCustomer,
      resolveManyShopCustomers,
      clearIdentityCache,
    ]
  );

  return createElement(
    IdentityBrokerContext.Provider,
    { value },
    children
  );
}

export function useIdentityBroker(): IdentityBrokerValue {
  const ctx = useContext(IdentityBrokerContext);
  if (!ctx) {
    throw new Error(
      'useIdentityBroker must be used within IdentityBrokerProvider'
    );
  }
  return ctx;
}
